import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FilterPanel from '../../FilterLayout/FilterPanel';

import useFilters from '../../../../../utilities/hooks/useFilters';
import { OptionType } from '../../../../../shared/types/filters';
import { GenderOptions } from '../../../../../shared/constants/filter/filtersOptions';
import { FilterKeys } from '../../../../../shared/constants/filter/filter';

interface IGenderArgs {
  customGender: boolean;
}

export default function GenderFilter({ customGender }: IGenderArgs) {
  const { genderFilter, setGenderFilter } = useFilters();
  const { t } = useTranslation();

  function getSelectedGenders() {
    const selected = [] as OptionType<string>[];

    genderFilter.forEach((gender) => {
      const res = GenderOptions.find((option) => option.value === gender);
      if (res) {
        selected.push({ label: t(res.label) ?? 'n/a', value: res.value });
      }
    });
    return selected;
  }

  function isGenderSelected(value: string) {
    return genderFilter.includes(value);
  }

  function onSelectedGendersChange(value: string) {
    if (genderFilter.includes(value)) {
      setGenderFilter(genderFilter.filter((val) => val !== value));
    } else {
      setGenderFilter([...genderFilter, value]);
    }
  }

  function selectAllGenders(
    isChecked: boolean,
    optionsToSelect: any,
    isMatching?: boolean,
  ) {
    if (isChecked || (!isChecked && optionsToSelect.length === 0)) {
      const updatedGenders = isMatching
        ? genderFilter.filter((id) => !optionsToSelect.includes(id))
        : [];
      setGenderFilter(updatedGenders);
    } else if (optionsToSelect.length > 0) {
      setGenderFilter(
        isMatching
          ? [...genderFilter, ...optionsToSelect.map(String)]
          : optionsToSelect,
      );
    } else {
      setGenderFilter(GenderOptions.map((option) => option.value));
    }
  }

  const localisedOptions = useMemo(
    () =>
      (customGender
        ? GenderOptions.filter(
            ({ label }) =>
              label !== 'filterGenderNonBinary' &&
              label !== 'filterGenderUnknown',
          )
        : GenderOptions
      ).map(({ label, value }) => ({
        label: t(label) ?? 'n/a',
        value,
      })),
    [customGender],
  );

  return (
    <FilterPanel<string>
      name="gender"
      filterKey={FilterKeys.gender}
      options={localisedOptions}
      isSelected={isGenderSelected}
      onSelectedChange={onSelectedGendersChange}
      selectedItems={getSelectedGenders()}
      selectAllItems={selectAllGenders}
      flatItemArray={localisedOptions}
    />
  );
}
